/**
 * Leitura da avaliação das regras que a Telli manda no acionamento.
 *
 * Formato real do webhook:
 *   evaluation_details: {
 *     evaluated_rule_sets: 5,
 *     rejections: [{ rule_set_id, rule_set_name, failures: ["origin_city mismatch: ..."] }]
 *   }
 * As mensagens vêm em inglês e coladas; aqui viram rótulo + explicação em
 * português, sem perder o texto original (fica no `bruto`, exibido no title).
 */

const CAMPOS = [
  [/^origin_city/, "Cidade de origem"],
  [/^origin_uf|^origin_state/, "UF de origem"],
  [/^origin_local/, "Bairro / CEP / zona de origem"],
  [/^origin_neighborhood/, "Bairro de origem"],
  [/^origin_cep|^origin_zip/, "CEP de origem"],
  [/^origin_zone/, "Zona de origem"],
  [/^destination_city/, "Cidade de destino"],
  [/^destination_uf|^destination_state/, "UF de destino"],
  [/^destination_local/, "Bairro / CEP / zona de destino"],
  [/^destination_neighborhood/, "Bairro de destino"],
  [/^destination_cep|^destination_zip/, "CEP de destino"],
  [/^service_type/, "Tipo de serviço"],
  [/^attendance_type/, "Tipo de acionamento"],
  [/^schedule/, "Janela de horário"],
  [/^vehicle/, "Veículo"],
  [/^contractor/, "Contratante"],
  [/^destination$/, "Destino"],
  [/^origin$/, "Origem"],
];

// Mensagens gerais (sem "campo mismatch") que o portal manda em inglês.
const MENSAGENS = [
  [/no active rule sets? configured/i, "Nenhum conjunto de regras ativo para este prestador no portal"],
  [/no rule sets?( found| configured)?/i, "Nenhum conjunto de regras configurado no portal"],
  [/rule set (is )?(inactive|disabled)/i, "Conjunto de regras inativo"],
  [/worker (is )?(inactive|disabled|offline)/i, "Prestador inativo no portal"],
  [/outside (of )?(the )?schedule|out of schedule/i, "Fora da janela de horário configurada"],
  [/destination filter is enabled but (the )?service has no destination/i, "Destino obrigatório: a regra filtra pelo destino, mas o acionamento não tem destino informado"],
  [/origin filter is enabled but (the )?service has no origin/i, "Origem obrigatória: a regra filtra pela origem, mas o acionamento não tem origem informada"],
  [/vehicle filter is enabled but (the )?service has no vehicle/i, "Veículo obrigatório: a regra filtra pelo veículo, mas o acionamento não tem veículo informado"],
  [/^([a-z_]+)_required\b/i, (m) => `${rotuloDoCampo(m[1])}: informação obrigatória ausente no acionamento`],
];
const traduzirMensagem = (txt) => {
  for (const [re, msg] of MENSAGENS) {
    const m = txt.match(re);
    if (m) return typeof msg === "function" ? msg(m) : msg;
  }
  return null;
};
const SEM_CONJUNTO = new Set(["", "none", "null", "n/a"]);

const rotuloDoCampo = (campo) =>
  CAMPOS.find(([re]) => re.test(campo))?.[1] ||
  campo.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

const listaEntreColchetes = (txt) => {
  const m = txt.match(/\[([^\]]*)\]/);
  if (!m) return "";
  return m[1].trim();
};

function explicar(campo, resto, bruto) {
  if (campo.startsWith("schedule")) {
    const hora = (bruto.match(/(\d{2}:\d{2})(:\d{2})?/) || [])[1];
    const qtd = (bruto.match(/all (\d+) configured/) || [])[1];
    if (hora) {
      return `${hora} está fora ${
        qtd ? (qtd === "1" ? "da janela configurada" : `das ${qtd} janelas configuradas`) : "das janelas configuradas"
      }`;
    }
  }
  if (campo.includes("local")) {
    const configurado = listaEntreColchetes(resto.split("did not match").pop() || "");
    return configurado
      ? `o endereço do acionamento não atende aos critérios configurados (${configurado})`
      : "o endereço do acionamento não atende aos critérios de bairro, CEP ou zona configurados";
  }
  const naoEsta = resto.match(/'([^']*)'\s*not in\s*\[([^\]]*)\]/i);
  if (naoEsta) {
    const permitidos = naoEsta[2].trim();
    return `“${naoEsta[1]}” não está entre: ${permitidos || "(nenhum valor configurado)"}`;
  }
  return resto || bruto;
}

export function analisarFalha(texto) {
  const bruto = String(texto || "").trim();
  const geral = !/mismatch/i.test(bruto) && traduzirMensagem(bruto);
  if (geral) return { rotulo: geral, detalhe: "", bruto };
  const campo = bruto.split(/\s+mismatch/i)[0].trim();
  const marcador = bruto.toLowerCase().indexOf("mismatch:");
  const resto = marcador >= 0 ? bruto.slice(marcador + "mismatch:".length).trim() : "";
  return { rotulo: rotuloDoCampo(campo), detalhe: explicar(campo, resto, bruto), bruto };
}

/** Resumo pronto para a tela: casou ou não, quem avaliou e o que faltou. */
export function analisarAvaliacao(det = {}) {
  const casou = String(det.evaluation_result || "").toUpperCase() === "MATCH";
  const ev = det.evaluation || {};
  const rejeicoes = Array.isArray(ev.rejections) ? ev.rejections : [];

  const semConjunto = (r) => SEM_CONJUNTO.has(String(r.rule_set_name ?? r.rule_set_id ?? "").trim().toLowerCase());
  // Rejeição fora de qualquer conjunto (ex.: prestador sem regras ativas) vira motivo geral.
  const motivos = rejeicoes.filter(semConjunto).flatMap((r) => (r.failures || []).map(analisarFalha));
  const conjuntos = rejeicoes
    .filter((r) => !semConjunto(r))
    .map((r) => ({
      id: r.rule_set_id || r.rule_set_name,
      nome: r.rule_set_name || "Conjunto sem nome",
      falhas: (r.failures || []).map(analisarFalha),
    }))
    .sort((a, b) => a.falhas.length - b.falhas.length);

  const avaliados = ev.evaluated_rule_sets ?? conjuntos.length;
  // Portal avaliou zero conjuntos: não casou por falta de regras ativas.
  if (!casou && Number(avaliados) === 0 && !conjuntos.length && !motivos.length) {
    motivos.push({
      rotulo: "Nenhuma regra cadastrada e ativa para avaliar",
      detalhe: "o portal não tinha conjunto de regras ativo para este prestador, por isso o acionamento não casou",
      bruto: JSON.stringify(ev),
    });
  }

  return {
    casou,
    conjunto: det.matched_rule_set_name || "",
    avaliados,
    conjuntos,
    maisProximo: conjuntos[0] || null,
    motivos,
    // Portal mandou algo que não reconhecemos: mostramos cru em vez de esconder.
    cru: !conjuntos.length && !motivos.length && Object.keys(ev).length ? ev : null,
  };
}
